import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import ConfirmDialog from "../components/ConfirmDialog";
import { CheckCircle2, XCircle, Loader2, Send, Trophy, Home, ArrowLeft, ChevronRight, RotateCcw } from "lucide-react";

function ResultView({ result, courseId }) {
  const navigate = useNavigate();
  const cid = courseId || result.course_id;
  const isIntermediate = result.test_type === "intermediate";

  // Prima lezione del modulo successivo, per proseguire senza passare dalla
  // pagina del corso. Si ricava dal riepilogo, che conosce l'ordine dei moduli.
  const [nextLessonId, setNextLessonId] = useState(null);
  // Tentativi residui sul test appena svolto: il pulsante "Ripeti" va offerto
  // solo se il server accetterebbe davvero un nuovo tentativo.
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!cid) return;
    let annullato = false;
    (async () => {
      try {
        const { data } = await api.get(`/me/courses/${cid}/progress`);
        if (annullato) return;
        const i = data.modules.findIndex((m) => m.id === result.module_id);

        if (result.passed && isIntermediate) {
          const first = i >= 0 ? data.modules[i + 1]?.lessons?.[0] : null;
          // Se fosse ancora bloccata, il pulsante porterebbe in un vicolo cieco
          if (first && !first.is_locked) setNextLessonId(first.id);
        }

        if (!result.passed && isIntermediate && i >= 0) {
          const mod = data.modules[i];
          setAttemptsLeft(
            (mod.intermediate_attempts_max ?? 3) - (mod.intermediate_attempts_used ?? 0)
          );
        }
      } catch {
        // Non bloccante: restano gli altri pulsanti
      }
    })();
    return () => { annullato = true; };
  }, [result.passed, result.module_id, isIntermediate, cid]);

  const retry = async () => {
    setRetrying(true);
    try {
      const path = isIntermediate
        ? `/tests/intermediate/${result.module_id}/start`
        : `/tests/final/${cid}/start`;
      const { data } = await api.post(path);
      navigate(`/studente/test/${data.attempt_id}`, { state: { start: data } });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        detail && typeof detail === "object"
          ? detail.message || formatApiError(err)
          : formatApiError(err)
      );
    } finally {
      setRetrying(false);
    }
  };

  // Al terzo tentativo fallito il server azzera il progresso del modulo: ripetere
  // subito non è possibile, vanno riviste le lezioni. Il pulsante non deve
  // comparire, altrimenti porterebbe a un errore.
  const canRetryIntermediate =
    !result.passed && isIntermediate && !result.module_reset && (attemptsLeft === null || attemptsLeft > 0);
  // Il test finale non ha limite di tentativi
  const canRetryFinal = !result.passed && !isIntermediate;
  const canRetry = canRetryIntermediate || canRetryFinal;

  return (
    <div data-testid="test-result" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div
          className={
            "rounded-sm border p-6 sm:p-10 text-center mb-8 " +
            (result.passed
              ? "bg-emerald-50 border-emerald-200"
              : "bg-red-50 border-red-200")
          }
        >
          <Trophy
            size={40}
            className={result.passed ? "mx-auto text-emerald-600 mb-3" : "mx-auto text-red-600 mb-3"}
          />
          <div className="text-xs uppercase tracking-widest font-semibold mb-2 text-brand-gold">
            Risultato — Tentativo #{result.attempt_number}
          </div>
          <div className="text-5xl sm:text-6xl font-bold text-brand-blue wordmark mb-2" data-testid="result-score">
            {result.score}%
          </div>
          <div className={result.passed ? "text-emerald-800 font-semibold text-lg" : "text-red-800 font-semibold text-lg"}>
            {result.passed ? "TEST SUPERATO" : "TEST NON SUPERATO"}
          </div>
          <div className="text-sm text-brand-muted mt-1">
            Risposte corrette: <strong>{result.correct_count} / {result.total}</strong> · Punteggio minimo:{" "}
            <strong>{result.passing_score}%</strong>
          </div>
        </div>

        <div className="space-y-4 mb-10">
          {result.results.map((r, i) => (
            <div
              key={r.question_id}
              data-testid={`result-question-${r.question_id}`}
              className={
                "bg-white border rounded-sm p-5 " +
                (r.is_correct ? "border-emerald-200" : "border-red-200")
              }
            >
              <div className="flex items-start gap-3 mb-3">
                {r.is_correct ? (
                  <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 shrink-0" />
                ) : (
                  <XCircle size={20} className="text-red-600 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-widest text-brand-muted">Domanda {i + 1}</div>
                  <div className="text-base text-brand-ink font-semibold">{r.text}</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                {r.options.map((opt, oi) => {
                  const isCorrect = opt.id === r.correct_option_id;
                  const isSelected = opt.id === r.selected_option_id;
                  let cls = "border-brand-line bg-brand-bg text-brand-ink";
                  if (isCorrect) cls = "border-emerald-300 bg-emerald-50 text-emerald-900";
                  else if (isSelected) cls = "border-red-300 bg-red-50 text-red-900";
                  return (
                    <div key={opt.id} className={"flex items-start gap-2 text-sm rounded-sm px-3 py-2 border " + cls}>
                      <span className="text-xs font-semibold w-4 shrink-0">{String.fromCharCode(65 + oi)}.</span>
                      <span className="flex-1">{opt.text}</span>
                      {isCorrect && <span className="text-[10px] font-semibold text-emerald-700">CORRETTA</span>}
                      {isSelected && !isCorrect && <span className="text-[10px] font-semibold text-red-700">TUA RISPOSTA</span>}
                    </div>
                  );
                })}
              </div>
              {r.explanation && (
                <div className="text-sm text-brand-muted italic bg-brand-bg border-l-2 border-brand-gold pl-3 py-1 mt-2">
                  {r.explanation}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Esito negativo: si spiega come proseguire prima ancora dei pulsanti */}
        {!result.passed && (
          <div
            data-testid="retry-hint"
            className="mb-6 rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            {result.module_reset ? (
              <>
                <strong>Hai esaurito i tentativi disponibili.</strong> Il progresso del
                modulo è stato azzerato: rivedi le lezioni per intero per sbloccare
                nuovamente il test.
              </>
            ) : canRetryIntermediate ? (
              <>
                Non hai raggiunto il punteggio minimo. Puoi ripetere il test
                {attemptsLeft !== null && (
                  <>
                    : ti {attemptsLeft === 1 ? "resta" : "restano"}{" "}
                    <strong>{attemptsLeft} {attemptsLeft === 1 ? "tentativo" : "tentativi"}</strong>
                  </>
                )}
                .
              </>
            ) : (
              <>Non hai raggiunto il punteggio minimo. Puoi ripetere il test quando vuoi.</>
            )}
          </div>
        )}

        <div className="flex gap-3 flex-wrap">
          {canRetry && (
            <button
              onClick={retry}
              disabled={retrying}
              data-testid="retry-test-btn"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {retrying ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Ripeti il test
            </button>
          )}
          {nextLessonId && (
            <button
              onClick={() => navigate(`/studente/corsi/${cid}/lezioni/${nextLessonId}`)}
              data-testid="next-module-after-test-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              Passa al modulo successivo <ChevronRight size={14} />
            </button>
          )}
          <button
            onClick={() => navigate(`/studente/corsi/${cid}`)}
            data-testid="back-to-course-btn"
            className={
              nextLessonId || canRetry
                ? "inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm"
                : "btn-primary inline-flex items-center gap-2"
            }
          >
            <ArrowLeft size={14} /> Torna al corso
          </button>
          <button
            onClick={() => navigate("/studente")}
            className="inline-flex items-center gap-2 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-4 py-2 font-semibold text-sm"
          >
            <Home size={14} /> Vai ai corsi
          </button>
        </div>
      </main>
    </div>
  );
}

export default function TakeTest() {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [start, setStart] = useState(location.state?.start || null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [loadingResult, setLoadingResult] = useState(false);

  useEffect(() => {
    // Ripetendo un test si cambia `attemptId` restando sulla stessa rotta: React
    // riusa il componente e lo stato sopravvive. Senza questo azzeramento, il
    // nuovo tentativo mostrerebbe il risultato di quello precedente.
    const nuovoStart = location.state?.start || null;
    setStart(nuovoStart);
    setResult(null);
    setAnswers({});

    // Senza dati di avvio (link diretto o ricaricamento) si recupera l'esito già
    // registrato per questo tentativo.
    if (!nuovoStart && attemptId) {
      (async () => {
        setLoadingResult(true);
        try {
          const { data } = await api.get(`/tests/attempts/${attemptId}/result`);
          setResult(data);
        } catch (e) {
          toast.error("Sessione test scaduta. Torna al corso e riavvia il test.");
          navigate("/studente");
        } finally {
          setLoadingResult(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  const submit = async () => {
    setSubmitting(true);
    setConfirmSubmit(false);
    try {
      const body = {
        answers: start.questions.map((q) => ({
          question_id: q.id,
          selected_option_id: answers[q.id] || null,
        })),
      };
      const { data } = await api.post(`/tests/attempts/${attemptId}/submit`, body);
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <ResultView result={result} courseId={start?.course_id} />;
  }

  if (loadingResult || !start) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <Topbar />
        <div className="text-center py-16 text-brand-muted">
          <Loader2 size={20} className="animate-spin inline mr-2" /> Caricamento…
        </div>
      </div>
    );
  }

  const answered = Object.values(answers).filter(Boolean).length;
  const total = start.questions.length;
  const progressPct = Math.round((answered / total) * 100);

  return (
    <div data-testid="take-test-page" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            {start.test_type === "final" ? "Test finale" : "Test intermedio"} — Tentativo #{start.attempt_number}
            {start.total_attempts_allowed ? ` / ${start.total_attempts_allowed}` : ""}
          </div>
          <h1 className="text-2xl sm:text-3xl text-brand-ink mb-1">Rispondi a tutte le domande</h1>
          <p className="text-sm text-brand-muted">
            Punteggio minimo per superare: <strong>{start.passing_score}%</strong>. Devi rispondere a tutte le domande per consegnare.
          </p>
        </div>

        {/* Progress */}
        <div className="sticky top-16 z-30 bg-brand-bg pt-1 pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex justify-between text-xs text-brand-muted mb-1.5">
            <span data-testid="progress-label">Risposte: {answered} / {total}</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-1.5 bg-brand-line rounded-sm overflow-hidden">
            <div className="h-full bg-brand-blue transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        <div className="space-y-5 mb-8">
          {start.questions.map((q, i) => (
            <div key={q.id} data-testid={`take-question-${q.id}`} className="bg-white border border-brand-line rounded-sm p-5">
              <div className="text-xs uppercase tracking-widest text-brand-gold">Domanda {i + 1} / {total}</div>
              <div className="text-base text-brand-ink font-semibold mt-1 mb-4">{q.text}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[q.id] === opt.id;
                  return (
                    <label
                      key={opt.id}
                      data-testid={`take-option-${q.id}-${opt.id}`}
                      className={
                        "flex items-start gap-3 border rounded-sm px-3 py-2.5 cursor-pointer transition-colors " +
                        (selected
                          ? "border-brand-blue bg-brand-blue/5"
                          : "border-brand-line hover:border-brand-blue/40")
                      }
                    >
                      <input
                        type="radio"
                        name={q.id}
                        checked={selected}
                        onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt.id }))}
                        className="mt-1 accent-brand-blue"
                      />
                      <span className="text-xs font-semibold w-4 shrink-0 text-brand-muted">{String.fromCharCode(65 + oi)}.</span>
                      <span className="text-sm flex-1">{opt.text}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 bg-brand-bg border-t border-brand-line py-4 flex justify-between items-center gap-3">
          <div className="text-sm text-brand-muted">
            {answered < total ? (
              <span className="text-amber-700">Mancano {total - answered} rispost{total - answered === 1 ? "a" : "e"}: rispondi a tutte per consegnare.</span>
            ) : (
              <span className="text-emerald-700">Tutte le domande risposte.</span>
            )}
          </div>
          <button
            onClick={() => setConfirmSubmit(true)}
            disabled={submitting || answered < total}
            data-testid="submit-test-btn"
            title={answered < total ? "Rispondi a tutte le domande per consegnare" : undefined}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Invia test
          </button>
        </div>
      </main>

      <ConfirmDialog
        open={confirmSubmit}
        onOpenChange={setConfirmSubmit}
        title="Inviare il test?"
        description="Confermi l'invio delle risposte? Dopo l'invio non potrai modificarle."
        confirmLabel="Sì, invia"
        onConfirm={submit}
        testid="confirm-submit-test"
      />
    </div>
  );
}
